import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  ChevronDown,
  CircleHelp,
  CloudUpload,
  Code2,
  Database,
  LockKeyhole,
  Menu,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Brand } from "../components/Brand";
import { ChatWidgetPreview } from "../components/ChatWidgetPreview";
import { appConfig, formatMoney } from "../lib/config";
import { emitLandingCta } from "../lib/productEvents";
import { CATALOG_PLANS } from "../lib/plans";

const faq = [
  {
    question: "Funciona em páginas públicas?",
    answer:
      "Não por padrão. No APEX, a região da Página 0 deve usar condição server-side para exibir o widget somente em páginas autenticadas e normais.",
  },
  {
    question: "Posso usar modelos diferentes por projeto?",
    answer:
      "Sim. Cada projeto mantém sua própria configuração. Há configuração gerenciada e opção de chave própria para administradores. Salvar uma configuração não comprova uma resposta; a conexão precisa ser testada.",
  },
  {
    question: "O usuário precisa criar outra conta?",
    answer:
      "Não. O APEX emite uma credencial curta e assinada. O usuário continua autenticado apenas na sua aplicação.",
  },
  {
    question: "O que acontece ao terminar o teste?",
    answer:
      "Após os 7 dias, o assistente é pausado. Minha conta, Ajuda e a gestão de membros e acesso, conforme seu papel, continuam disponíveis. Fale com suporte@4apex.chat sobre continuidade. Não há cobrança automática nem contratação online nesta fase.",
  },
];

function ProductStage() {
  return (
    <div className="hero-product" aria-label="Prévia do 4apex no Oracle APEX">
      <div className="apex-window">
        <header>
          <strong className="apex-brand" aria-label="4apex">
            <svg viewBox="0 0 40 40" aria-hidden="true">
              <defs>
                <linearGradient
                  id="apexora-brand-hero"
                  x1="4"
                  y1="4"
                  x2="36"
                  y2="36"
                >
                  <stop stopColor="#26c6e8" />
                  <stop offset=".48" stopColor="#7251f7" />
                  <stop offset="1" stopColor="#4b1de1" />
                </linearGradient>
              </defs>
              <path
                d="M20 3.5 35.4 12v16L20 36.5 4.6 28V12L20 3.5Zm0 6.1-9.9 5.45v9.9L20 30.4l9.9-5.45v-9.9L20 9.6Z"
                fill="url(#apexora-brand-hero)"
              />
              <path
                d="m10.1 15.05 9.9 5.46 9.9-5.46v5.7L20 26.2l-9.9-5.45v-5.7Z"
                fill="white"
                fillOpacity=".9"
              />
            </svg>
            4apex
          </strong>
          <div className="apex-search">Pesquisar…</div>
          <span className="apex-avatar">AM</span>
        </header>
        <div className="apex-window__layout">
          <aside>
            <b>Dashboard</b>
            <span>Clientes</span>
            <span>Pedidos</span>
            <span>Produtos</span>
            <span>Financeiro</span>
            <span>Relatórios</span>
          </aside>
          <div className="mock-main">
            <h3>Dashboard</h3>
            <div className="mock-metrics">
              <div>
                <span>Pedidos (mês)</span>
                <strong>1.248</strong>
                <small>↑ 12,5%</small>
              </div>
              <div>
                <span>Faturamento (mês)</span>
                <strong>R$ 2,48M</strong>
                <small>↑ 8,3%</small>
              </div>
            </div>
            <div className="mock-chart">
              <svg viewBox="0 0 420 150" aria-hidden="true">
                <path
                  d="M0 125 C40 90 70 112 105 82 S180 104 215 55 S280 76 320 38 S375 57 420 20"
                  fill="none"
                  stroke="#6238f5"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
                <path
                  d="M0 125 C40 90 70 112 105 82 S180 104 215 55 S280 76 320 38 S375 57 420 20 V150 H0Z"
                  fill="url(#hero-chart)"
                />
                <defs>
                  <linearGradient id="hero-chart" x1="0" y1="0" x2="0" y2="1">
                    <stop stopColor="#7251f7" stopOpacity=".22" />
                    <stop offset="1" stopColor="#7251f7" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div className="hero-thread" aria-hidden="true">
        <i />
      </div>
      <div className="hero-chat" id="demo">
        <ChatWidgetPreview />
      </div>
      <div className="hero-code">
        <div>
          <Code2 size={16} />
          <strong>Snippet da Página 0</strong>
          <span>Exemplo</span>
        </div>
        <code>
          &lt;<em>apexora-widget</em>{" "}
          project-key="..."&gt;&lt;/apexora-widget&gt;
        </code>
      </div>
    </div>
  );
}

export function LandingPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="landing">
      <header className="landing-nav">
        <Brand />
        <nav className={mobileOpen ? "is-open" : ""} aria-label="Principal">
          <a href="#product">Produto</a>
          <a href="#how">Como funciona</a>
          <a href="#pricing">Preço</a>
          <a href="#help">Ajuda</a>
          <div className="landing-nav__mobile-actions">
            <Link
              className="button button--ghost"
              to="/auth"
              onClick={() => setMobileOpen(false)}
            >
              Entrar
            </Link>
            <Link
              className="button button--primary"
              to="/auth?mode=signup"
              onClick={() => {
                emitLandingCta("navigation");
                setMobileOpen(false);
              }}
            >
              Começar grátis <ArrowRight size={17} />
            </Link>
          </div>
        </nav>
        <div className="landing-nav__actions">
          <Link className="button button--ghost" to="/auth">
            Entrar
          </Link>
          <Link
            className="button button--primary"
            to={"/auth?mode=signup"}
            onClick={() => emitLandingCta("navigation")}
          >
            Começar grátis <ArrowRight size={17} />
          </Link>
        </div>
        <button
          type="button"
          className="landing-nav__menu"
          onClick={() => setMobileOpen((value) => !value)}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
        >
          {mobileOpen ? <X /> : <Menu />}
        </button>
      </header>

      <main>
        <section className="hero" id="product">
          <div className="hero__copy">
            <h1>
              Ajude seus usuários a avançar <span>dentro do Oracle APEX.</span>
            </h1>
            <p>
              Transforme a documentação do seu produto em respostas com fontes
              dentro da aplicação. Revise as dúvidas e melhore o conhecimento de
              cada projeto.
            </p>
            <div className="hero__actions">
              <Link
                className="button button--primary button--large"
                to={"/auth?mode=signup"}
                onClick={() => emitLandingCta("hero")}
              >
                <Sparkles size={18} />
                Criar meu assistente
              </Link>
              <a className="button button--secondary button--large" href="#how">
                <CircleHelp size={18} />
                Ver como funciona
              </a>
            </div>
            <div className="hero__trust">
              <ShieldCheck size={18} />
              <span>{appConfig.trialDays} dias grátis</span>
              <i />
              <span>Sem cartão</span>
              <i />
              <span>Sem cobrança automática</span>
            </div>
          </div>
          <ProductStage />
        </section>

        <p className="demo-notice">
          Demonstração com conteúdo de exemplo, sem consultar dados da sua
          empresa. Para instalar: acesso de desenvolvimento ao APEX, páginas
          autenticadas e configuração no servidor.{" "}
          <Link to="/help?article=apex">Veja os pré-requisitos</Link>.
        </p>
        <section className="product-proof">
          <div className="section-heading">
            <h2>
              Um assistente que entende <span>seu produto.</span>
            </h2>
            <p>
              Orientações baseadas no conhecimento publicado. O assistente não
              consulta livremente as tabelas do ERP nem executa transações.
            </p>
          </div>
          <div className="proof-rail">
            <div>
              <Database />
              <strong>Conhecimento sob controle</strong>
              <span>Documentos versionados e separados por projeto.</span>
            </div>
            <div>
              <LockKeyhole />
              <strong>Identidade protegida</strong>
              <span>
                O usuário utiliza a identidade validada pela sua aplicação APEX.
              </span>
            </div>
            <div>
              <BarChart3 />
              <strong>Melhoria contínua</strong>
              <span>
                Revise conversas, fontes e avaliações para melhorar o conteúdo.
              </span>
            </div>
          </div>
        </section>

        <section className="workflow-section" id="how">
          <div className="section-heading section-heading--center">
            <h2>
              Da documentação à resposta, <span>com cada etapa visível.</span>
            </h2>
          </div>
          <div className="workflow">
            <div className="workflow__line" aria-hidden="true" />
            {[
              {
                icon: CloudUpload,
                title: "1. Conecte o conhecimento",
                body: "Envie arquivos TXT ou Markdown e publique o conteúdo no projeto certo.",
              },
              {
                icon: Code2,
                title: "2. Configure a integração APEX",
                body: "Configure identidade no servidor, autorize a origem e inclua o widget nas páginas permitidas.",
              },
              {
                icon: BarChart3,
                title: "3. Revise e melhore",
                body: "Veja dúvidas, avaliações e lacunas para melhorar continuamente.",
              },
            ].map(({ icon: Icon, title, body }, index) => (
              <article key={title}>
                <span className="workflow__number">{index + 1}</span>
                <Icon className="workflow__icon" />
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="isolation-section" id="knowledge">
          <div className="isolation-section__copy">
            <h2>
              Feito para o seu produto. Isolado <span>por projeto.</span>
            </h2>
            <p>
              Cada produto tem sua base e histórico. A ilustração mostra
              projetos de exemplo, sem dados de clientes.
            </p>
            <ul>
              <li>
                <Database /> Conhecimento separado
              </li>
              <li>
                <ShieldCheck /> JWT de curta duração
              </li>
              <li>
                <UserRound /> Histórico por usuário
              </li>
              <li>
                <Sparkles /> Modelos configuráveis
              </li>
            </ul>
          </div>
          <div className="knowledge-mock">
            <aside>
              <Brand inverse />
              <small>Projeto atual</small>
              <div className="mock-control">
                <b>S</b> Simplifica <ChevronDown size={15} />
              </div>
              <div className="mock-control is-muted">
                <b>P</b> PerformaZap
              </div>
              <hr />
              <span>Visão geral</span>
              <span className="is-active">Fontes de conhecimento</span>
              <span>Conversas</span>
              <span>Configurações</span>
            </aside>
            <div className="mock-main">
              <header>
                <div>
                  <h3>Fontes de conhecimento</h3>
                  <p>Conteúdo publicado e disponível para o assistente.</p>
                </div>
                <span className="mock-control">
                  <CloudUpload size={16} /> Adicionar fonte (ilustração)
                </span>
              </header>
              <div className="knowledge-tabs">
                <b>Todas</b>
                <span>Documentos</span>
                <span>Conteúdo</span>
              </div>
              {[
                ["guia-integracao-apex.md", "Documento", "256 trechos"],
                ["politicas-de-uso.md", "Documento", "184 trechos"],
                ["manual-operacional.md", "Documento", "342 trechos"],
                ["faq.md", "Processando", "62%"],
              ].map(([name, type, count], index) => (
                <div className="knowledge-row" key={name}>
                  <BookOpen />
                  <div>
                    <strong>{name}</strong>
                    <span>
                      Atualizado {index === 0 ? "há 2 horas" : "recentemente"}
                    </span>
                  </div>
                  <small>{type}</small>
                  <span
                    className={type === "Processando" ? "is-processing" : ""}
                  >
                    {type === "Processando" ? "● " : "● Processado • "}
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pricing-section" id="pricing">
          <div className="pricing-heading">
            <h2>
              Comece pelo teste. <span>Conheça o catálogo de referência.</span>
            </h2>
            <p>
              Comece grátis por {appConfig.trialDays} dias com 1 projeto e 1
              instalação. Ao terminar, o assistente é pausado. A contratação
              online ainda não está disponível; os valores abaixo são
              referência, sem cobrança automática ou extensão de teste.
            </p>
          </div>
          <div className="pricing-grid">
            {CATALOG_PLANS.map((plan) => (
              <article
                className={
                  plan.highlight ? "pricing-card is-highlight" : "pricing-card"
                }
                key={plan.code}
              >
                {plan.highlight ? (
                  <span className="pricing-card__badge">
                    Catálogo de referência
                  </span>
                ) : null}
                <header>
                  <strong>{plan.name}</strong>
                  <div className="price">
                    <b>{formatMoney(plan.priceCents)}</b>
                    <span>/mês</span>
                  </div>
                  <p>{plan.tagline}</p>
                </header>
                <ul>
                  {plan.features.map((feature) => (
                    <li key={feature}>
                      <Check size={17} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  className="button button--primary"
                  to={"/auth?mode=signup"}
                  onClick={() => emitLandingCta("plans")}
                >
                  Criar meu assistente
                  <ArrowRight size={17} />
                </Link>
              </article>
            ))}
          </div>
          <div className="pricing-free-note">
            <ShieldCheck size={16} />
            <span>
              {appConfig.trialDays} dias grátis • 1 projeto • 1 instalação. Sem
              cartão para começar. Sem cobrança automática.
            </span>
          </div>
        </section>

        <section className="help-section" id="help">
          <div className="help-section__links">
            <h2>
              Você não precisa descobrir <span>sozinho.</span>
            </h2>
            {[
              "Guia de instalação no APEX",
              "Como proteger o contexto com JWT",
              "Como preparar a base de conhecimento",
              "Entender conversas e avaliações",
            ].map((label, index) => (
              <Link
                key={label}
                to={`/help?article=${["apex", "seguranca", "conhecimento", "primeiros-passos"][index]}`}
              >
                {index === 0 ? (
                  <BookOpen />
                ) : index === 1 ? (
                  <ShieldCheck />
                ) : index === 2 ? (
                  <Database />
                ) : (
                  <MessageCircle />
                )}
                {label}
                <ArrowRight size={16} />
              </Link>
            ))}
          </div>
          <div className="faq-list">
            {faq.map((item, index) => (
              <div
                className={openFaq === index ? "is-open" : ""}
                key={item.question}
              >
                <button
                  type="button"
                  aria-expanded={openFaq === index}
                  aria-controls={`faq-${index}`}
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                >
                  {item.question}
                  <ChevronDown size={18} />
                </button>
                <p id={`faq-${index}`} hidden={openFaq !== index}>
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="final-cta">
          <div className="final-cta__thread" aria-hidden="true" />
          <h2>
            Seu usuário pergunta.
            <br />O seu produto <span>responde.</span>
          </h2>
          <Link
            className="button button--primary button--large"
            to={"/auth?mode=signup"}
            onClick={() => emitLandingCta("footer")}
          >
            Criar meu assistente <ArrowRight size={18} />
          </Link>
        </section>
      </main>

      <footer className="landing-footer">
        <Brand inverse />
        <nav>
          <a href="#product">Produto</a>
          <Link to="/help">Documentação</Link>
          <Link to="/security">Segurança</Link>
          <Link to="/privacy">Privacidade</Link>
          <Link to="/terms">Termos</Link>
        </nav>
        <span>© 2026 {appConfig.name}</span>
      </footer>
    </div>
  );
}
